import { cn } from '@/utils/cn';
import { useEffect, useId, useState } from 'react';
import { useDragToClose } from '@/hooks/useDragToClose';
import { getDragTransform } from '@/utils/drag';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scroll';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export const Drawer = ({ isOpen, onClose, children, className }: DrawerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { dragState, handleMouseDown, handleTouchStart } = useDragToClose({ onClose });
  // Own token so releasing this drawer's lock never tears down a lock a sibling
  // overlay (e.g. an open `CheckoutBottomSheet`) still holds. See `scroll.ts`.
  const scrollLockId = useId();

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      lockBodyScroll(scrollLockId);
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      unlockBodyScroll(scrollLockId);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }

    return () => {
      unlockBodyScroll(scrollLockId);
    };
  }, [isOpen, scrollLockId]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen && !isVisible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-120',
        'transition-opacity duration-300 ease-in-out',
        isAnimating ? 'opacity-100' : 'opacity-0'
      )}
    >
      <div
        className={cn(
          'absolute inset-0 bg-popover-background/40 backdrop-blur-sm',
          'transition-opacity duration-300 ease-in-out',
          isAnimating ? 'opacity-100' : 'opacity-0'
        )}
        onClick={handleBackdropClick}
      />

      <div
        className={cn(
          'absolute bottom-0 left-0 right-0',
          'bg-white rounded-t-2xl shadow-2xl',
          'max-h-[90vh] flex flex-col',
          'transition-all duration-300 ease-out',
          !dragState.isDragging && isAnimating
            ? 'translate-y-0'
            : !dragState.isDragging && !isAnimating
              ? 'translate-y-full'
              : '',
          className
        )}
        style={{
          transform: dragState.isDragging ? getDragTransform(dragState) : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="w-24 h-1 bg-base-muted rounded-full" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4 pt-1">{children}</div>
      </div>
    </div>
  );
};
